import {
  Flex,
  Spacer,
  Skeleton,
  Box,
  useToast,
  CardBody,
  Card,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  AccountDetailsGQLType,
  TAccountTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartCards } from '../shared/PortfolioChartCards';
import {
  chartsInitialEndDateJs,
  chartsInitialEndDateMillis,
  chartsInitialStartDateJs,
  chartsInitialStartDateMillis,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  formatLedgerToChartData,
  getTAccountTypesFromLedgerData,
  onSelectDateRange,
  onToggleActiveField,
  PortfolioChartConfig,
  PortfolioSingleChartConfig,
  StatementsInsight,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { PortfolioTimePeriodSelectButtons } from '../shared/PortfolioTimePeriodSelectButtons';
import { colors, transparency } from '~/packages/common-frontend/colors';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import {
  getDefaultDashboardDisplayEndDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetLedgerQuery } from '~/packages/statements/requests/ledger.gql.generated';
import { truncateLiabilityAccountType } from '~/packages/statements/ui/statementsUtils';

export const generateExpensesFeesChartConfig = (
  accountTypes: TAccountTypeGQLType[],
): PortfolioChartConfig<string> => {
  const lineColors = [...Object.values(colors.chart)];

  const expensesFeesChartConfigs: Partial<
    Record<TAccountTypeGQLType, PortfolioSingleChartConfig>
  > = {};

  _.forEach(accountTypes, (accountType) => {
    expensesFeesChartConfigs[accountType] = {
      chartType: 'line',
      field: accountType,
      label: truncateLiabilityAccountType(accountType),
      color: `${lineColors[0] ?? colors.brick[300]}${transparency[50]}`,
      format: 'currency',
    };
    lineColors.shift();
  });

  return expensesFeesChartConfigs;
};

type PortfolioStatementsExpensesFeesChartProps = {
  accounts: AccountDetailsGQLType[];
  creditFacilityId: string;
  organizationIds: string[];
  chartTitle: string;
};
export const PortfolioStatementsExpensesFeesChart = ({
  creditFacilityId,
  organizationIds,
  accounts,
  chartTitle,
}: PortfolioStatementsExpensesFeesChartProps) => {
  const toast = useToast();

  const allAccountTypes = _.uniq(_.map(accounts, ({ tAccount }) => tAccount));

  const [selectedBar, setSelectedBar] = useState<StatementsInsight | null>(
    null,
  );
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    StatementsInsight[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    StatementsInsight[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );
  const [activeFields, setActiveFields] = useState<(keyof StatementsInsight)[]>(
    allAccountTypes,
  );
  const [
    tAccountsForCapitalProvider,
    setTAccountsForCapitalProvider,
  ] = useState<TAccountTypeGQLType[]>([]);

  // generate the chart config using the updated specific tAccounts that contain ledger data for capital provider
  // and not all of the tAccounts that ar in the expenses & fees category
  const chartConfig = useMemo(() => {
    return generateExpensesFeesChartConfig(tAccountsForCapitalProvider);
  }, [tAccountsForCapitalProvider]);

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<StatementsInsight>(
        chartDataWithTimePeriod,
        activeFields,
      ),
    [chartDataWithTimePeriod, activeFields],
  );

  const {
    refetch: refetchLedger,
    data: ledgerData,
    loading: isLedgerLoading,
  } = useGetLedgerQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      organizationIds,
      accountTypes: allAccountTypes,
      startDate: chartsInitialStartDateMillis,
      endDate: chartsInitialEndDateMillis,
    },
    onCompleted: (data) => {
      const statementsChartData = formatLedgerToChartData(data.ledger);

      // we must set the updated taccount types that actually have ledger data
      // based on the logged in capital provider organization id
      // instead of all account types that fall under expenses&fees
      const tAccountTypes = getTAccountTypesFromLedgerData(data.ledger);
      if (!tAccountsForCapitalProvider.length) {
        setTAccountsForCapitalProvider(tAccountTypes);
      }

      // since the selected / active chart cards are dynamic based on the tAccounts that come back from the chart data, we must set them every time the data is updated
      setActiveFields(tAccountTypes);

      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: statementsChartData,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate({
          chartData: statementsChartData,
          selectedTimePeriod,
        });
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isLedgerLoading || !ledgerData) {
    return <Skeleton height="700px" />;
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate({
      chartData: chartDataForDateRange,
      selectedTimePeriod: timePeriod,
    });

    setChartDataWithTimePeriod(formattedChartData);
  };

  return (
    <Box mb="40px">
      <Card variant="outline" size="lg">
        <CardBody display="flex">
          <Box width="70%">
            <Flex mb="40px" wrap="wrap" gap="0.5rem">
              <FullDateRangePicker
                onSelectDateRange={(newDateRange) => {
                  let refetchVariables;
                  if (newDateRange[0] && newDateRange[1]) {
                    refetchVariables = {
                      creditFacilityId,
                      organizationIds,
                      accountTypes: allAccountTypes, // refetch with all account types before narrowing down to tAccountsForCapitalProvider after fetch is complete
                      startDate: commonDate.dateToEpoch(newDateRange[0]),
                      endDate: commonDate.dateToEpoch(newDateRange[1]),
                    };
                  }
                  if (!newDateRange[0] && !newDateRange[1]) {
                    refetchVariables = {
                      creditFacilityId,
                      organizationIds,
                      accountTypes: allAccountTypes, // refetch with all account types before narrowing down to tAccountsForCapitalProvider after fetch is complete
                      startDate: chartsInitialStartDateMillis,
                      endDate: chartsInitialEndDateMillis,
                    };
                  }
                  return onSelectDateRange({
                    newDateRange,
                    refetch: refetchLedger,
                    refetchVariables,
                    creditFacilityId,
                    setChartDataDateRange,
                  });
                }}
                currentDateRange={{
                  startDate: chartDataDateRange.chartDataStartDate,
                  endDate: chartDataDateRange.chartDataEndDate,
                }}
                width="lg"
              />
              <Spacer />
              <PortfolioTimePeriodSelectButtons
                currentSelectedTimePeriod={selectedTimePeriod}
                onSelectTimePeriod={onSelectTimePeriod}
              />
            </Flex>
            {/* child 1 */}
            <PortfolioReChart
              chartData={chartDataWithTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
              currentSelectedBarReportingDate={
                selectedBar
                  ? selectedBar.reportingDate
                  : getDefaultDashboardDisplayEndDate()
              }
              onSelectBar={setSelectedBar}
              chartConfig={chartConfig}
              visibleChartComponents={activeFields}
            />
          </Box>
          <Spacer />
          {/* child 2 */}
          {selectedBar && (
            <Flex width="25%" flexDirection="column">
              <PortfolioChartCards
                selectedBarData={selectedBar}
                chartConfig={chartConfig}
                activeFields={activeFields}
                onToggleActiveField={(selectedField) =>
                  onToggleActiveField({
                    selectedField,
                    currentActiveFields: activeFields,
                    setActiveFields,
                  })
                }
              />
              <Spacer />

              <PortfolioDownloadCsvLink
                dataRows={dataRows}
                headers={headers}
                chartTitle={chartTitle}
                selectedTimePeriod={selectedTimePeriod}
              />
            </Flex>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};
